import * as z from 'zod';

import { BorrowerId, LoanId, LosId, PoolId } from '../BrandedIds';
import { PaymentStatus } from '../loan/Payments';
import { BalancePaymentSnapshot } from '../loan/Snapshots';

export interface LoanTransaction {
  id: string; // polymorphic, can be a PaymentId
  referenceId?: string;
  loanId: LoanId;
  borrowerId?: BorrowerId;
  poolId?: PoolId;
  borrowerLastName: string;
  timestamp: Date;
  processedDate?: Date;
  failureDate?: Date;
  failureCode?: string;
  type: TransactionType;
  status: PaymentStatus;
  method: TransactionMethod;
  disbursementType: DisbursementType;
  amount: number;
  expectedPayment: number;
  principal: number;
  additionalPrincipal: number;
  interest: number;
  escrow: number;
  fee: number;
  advance: number;
  advancePrincipal: number;
  advanceInterest: number;
  suspense: number;
  unappliedSuspense: number;
  holdback: number;
  reserve: number;
  losId: LosId;
  entityName?: string;
  dueDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  balanceSnapshot?: BalancePaymentSnapshot;
  changeId: number;
  deferredPrincipal: number;
  deferredInterest: number;
  deferredFee: number;
  achBatch?: string;
  disbursementRecipient?: string;
  comment?: string;
}

export const TransactionType = z.enum([
  'payment',
  'paymentReversal',
  'disbursement',
  'disbursementReversal',
  'sweep',
  'draw',
  'drawReversal',
  'payoff',
  'partialPayoff',
  'payoffReversal',
  'boarding',
  'fee',
  'feeReversal',
  'paymentDeferment',
  'paymentDefermentReversal',
  'deposit',
  'depositReversal',
  'principalModification',
  'principalModificationReversal',
  'writeoff',
]);
export type TransactionType = z.infer<typeof TransactionType>;

export const DisbursementType = z
  .enum(['servicingTransfer', 'escrow', 'advance', 'payoff', 'forwardPayment', 'paymentAfterPayoff'])
  .optional();
export type DisbursementType = z.infer<typeof DisbursementType>;

export const TransactionMethod = z
  .enum([
    'CHECK',
    'WIRE',
    'PHONE',
    'CREDIT_CARD',
    'DEBIT_CARD',
    'ACH',
    'AUTHORIZED_ACH',
    'RESERVE',
    'BALANCE_FORWARD',
    'PRINCIPAL_PREPAYMENT',
    'BALANCE_REALLOCATION',
  ])
  .optional();
export type TransactionMethod = z.infer<typeof TransactionMethod>;

export const isAchTransactionMethod = (method: TransactionMethod): boolean =>
  !!method && ['ACH', 'AUTHORIZED_ACH'].includes(method);
